/**
 * تبدیلِ پالت به «متغیرهای CSS».
 *
 * متغیر CSS یعنی چه: مرورگر اجازه می‌ده یه مقدار رو یک‌جا اسم‌گذاری کنی
 * (`--bg: #FFF9F2`) و همه‌جا با `var(--bg)` صداش بزنی. عوض‌کردنِ همون یک خط،
 * رنگِ کلِ صفحه رو عوض می‌کنه — بدونِ رندرِ دوباره‌ی React. سوئیچِ تمِ سایت
 * دقیقاً همینه: فقط `data-theme` روی تگِ <html> عوض می‌شه.
 *
 * چرا این CSS رو در زمان اجرا از روی توکن‌ها می‌سازیم و دستی نمی‌نویسیم:
 * تا هیچ‌وقت یه رنگ در `web.ts` عوض نشه ولی در فایلِ CSS جا بمونه.
 */
import { fonts, radius, space } from './scale.ts';
import { WEB_PALETTES } from './web.ts';
import type { WebPalette } from './palette.ts';

/** `brandText` → `--brand-text` */
const varName = (key: string): string =>
  `--${key.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`)}`;

/** فقط رنگ‌ها؛ `isDark` یه پرچمِ منطقیه و جایی در CSS نداره. */
export const paletteVars = (p: WebPalette): string =>
  (Object.keys(p) as (keyof WebPalette)[])
    .filter((k) => k !== 'isDark')
    .map((k) => `  ${varName(k)}: ${p[k]};`)
    .join('\n');

/** اندازه‌ها و فونت‌ها — بینِ دو تم مشترک‌اند، پس فقط یک‌بار نوشته می‌شن. */
export const scaleVars = (): string =>
  [
    ...Object.entries(radius).map(([k, v]) => `  --radius-${k}: ${v}px;`),
    ...[1, 2, 3, 4, 5, 6, 8].map((n) => `  --space-${n}: ${space(n)}px;`),
    `  --font-head: ${fonts.head};`,
    `  --font-sans: ${fonts.sans};`,
  ].join('\n');

/**
 * کلِ استایل‌شیتِ توکن‌ها. سایت موقع بوت این رشته رو داخل یه <style> می‌ذاره.
 *
 * حالتِ روشن پیش‌فرضه (روی `:root`)؛ حالتِ تیره فقط وقتی فعال می‌شه که
 * `data-theme="dark"` روی <html> باشه — یعنی انتخابِ صریحِ کاربر. حدسِ اولیه از
 * روی تنظیمِ سیستم را خودِ اپِ وب می‌زند و همان صفت را می‌گذارد؛ اینجا
 * `prefers-color-scheme` عمداً نیست تا یک منبعِ تصمیم بیشتر نداشته باشیم.
 */
export const themeCss = (): string =>
  [
    ':root {',
    scaleVars(),
    paletteVars(WEB_PALETTES.light),
    '}',
    '',
    ':root[data-theme="dark"] {',
    paletteVars(WEB_PALETTES.dark),
    '}',
  ].join('\n');
